import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, AlertTriangle, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import DashboardLayout from '../components/DashboardLayout';
import AnalyzerReport from '../components/AnalyzerReport';
import api from '../services/api';
import { toast } from 'sonner';

/**
 * /analyze/:id — saved analysis viewer.
 * Re-uses AnalyzerReport. Adds back navigation to the hub.
 */
const AnalyzeReportPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [analysis, setAnalysis] = useState(null);
  const [error, setError] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.getMovementAnalysis(id);
        if (alive) setAnalysis(res.data);
      } catch (e) {
        if (alive) setError(e?.response?.status === 404 ? 'Analysis not found.' : 'Could not load this analysis.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [id]);

  const onDelete = async () => {
    if (!window.confirm('Delete this analysis? The clip will be removed too.')) return;
    setDeleting(true);
    try {
      await api.deleteMovementAnalysis(id);
      toast.success('Analysis deleted.');
      navigate('/analyze');
    } catch (e) {
      toast.error('Could not delete the analysis.');
      setDeleting(false);
    }
  };

  // Build an authenticated streaming URL for the stored clip if any.
  // <video> tags cannot send headers, so we pass the JWT as a query param.
  const videoSrc = (() => {
    if (!analysis?.video_storage_path) return null;
    const token = localStorage.getItem('hpme_token');
    const backend = process.env.REACT_APP_BACKEND_URL;
    if (!token || !backend) return null;
    return `${backend}/api/video-analysis/${analysis.id}/video?auth=${encodeURIComponent(token)}`;
  })();

  return (
    <DashboardLayout>
      <div className="p-4 md:p-8 max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <Link to="/analyze" className="inline-flex items-center gap-2 text-xs text-zinc-400 hover:text-white" data-testid="analyze-back-link">
            <ArrowLeft className="w-3 h-3" /> All analyses
          </Link>
          {analysis && !loading && !error && (
            <button
              onClick={onDelete}
              disabled={deleting}
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-sm text-zinc-400 hover:text-red-400 hover:bg-red-500/10 transition-colors"
              data-testid="analyze-report-delete"
            >
              {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
              Delete analysis
            </button>
          )}
        </div>
        {loading && (
          <div className="hpme-card p-6 flex items-center gap-3 text-sm text-zinc-400" data-testid="analyze-report-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading analysis…
          </div>
        )}
        {error && !loading && (
          <div className="hpme-card p-6 flex items-center gap-3 text-sm text-red-400" data-testid="analyze-report-error">
            <AlertTriangle className="w-4 h-4" /> {error}
            <Link to="/analyze" className="ml-auto">
              <Button variant="ghost" size="sm">Back</Button>
            </Link>
          </div>
        )}
        {!loading && !error && analysis && (
          <AnalyzerReport analysis={analysis} videoURL={videoSrc} />
        )}
      </div>
    </DashboardLayout>
  );
};

export default AnalyzeReportPage;
